'use client';

import React from 'react';
import AppIcon from '@/components/ui/AppIcon';
import { useNotifications, type NotificationType } from '@/lib/notificationContext';

const TYPE_LABELS: Record<NotificationType, string> = {
  order_status: 'Order Status',
  approval: 'Approval',
  low_stock: 'Low Stock',
  invoice_payment: 'Invoice Payment',
};

const TYPE_BADGE: Record<NotificationType, string> = {
  order_status: 'bg-blue-100 text-blue-700',
  approval: 'bg-purple-100 text-purple-700',
  low_stock: 'bg-amber-100 text-amber-700',
  invoice_payment: 'bg-green-100 text-green-700',
};

export default function ActivityLogPanel() {
  const { showLogPanel, setShowLogPanel, activityLog, unreadCount, markAllRead, clearLog } = useNotifications();

  if (!showLogPanel) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[998] bg-black/20"
        onClick={() => setShowLogPanel(false)}
      />
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-96 z-[999] bg-card border-l border-border shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <AppIcon name="BellIcon" size={18} className="text-primary" />
            <h2 className="text-base font-700 text-foreground">Activity Log</h2>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-700">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowLogPanel(false)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <AppIcon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Actions */}
        {activityLog.length > 0 && (
          <div className="flex items-center gap-3 px-5 py-2.5 border-b border-border shrink-0">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-primary font-500 hover:underline"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={clearLog}
              className="text-xs text-muted-foreground font-500 hover:text-red-600 ml-auto"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Log entries */}
        <div className="flex-1 overflow-y-auto">
          {activityLog.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <AppIcon name="BellSlashIcon" size={22} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-500 text-muted-foreground">No activity yet</p>
              <p className="text-xs text-muted-foreground">Notifications for order changes, approvals, stock alerts, and payments will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {activityLog.map(entry => (
                <div
                  key={entry.id}
                  className={`px-5 py-3.5 transition-colors ${entry.read ? 'bg-card' : 'bg-primary/5'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${entry.read ? 'bg-muted' : 'bg-primary/10'}`}>
                      <AppIcon name={entry.icon} size={15} className={entry.read ? 'text-muted-foreground' : entry.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className={`text-sm leading-tight ${entry.read ? 'font-500 text-foreground' : 'font-700 text-foreground'}`}>
                          {entry.title}
                        </p>
                        {!entry.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-snug">{entry.message}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-xs font-500 px-1.5 py-0.5 rounded-md ${TYPE_BADGE[entry.type]}`}>
                          {TYPE_LABELS[entry.type]}
                        </span>
                        <span className="text-xs text-muted-foreground">{entry.timestamp}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
